/**
 * The external-campaign import modal (#6381 chunk E, reshaped in #6492).
 *
 * One step and one decision: run the named eligibility checks, show each outcome,
 * and either import or explain why the campaign cannot be imported yet (default
 * fail). The campaign type used to be a second step here, asking the user to pick
 * a type or defer to detection before any structure existed to judge one from.
 * Detection now applies its own verdict once the structure lands (#6492), so the
 * question is answered rather than asked, and a wrong guess is corrected on the
 * Overview.
 *
 * Because the applied type completes adoption, importing IS the act that makes a
 * campaign fully managed. That consequence is stated here, on the button the user
 * actually presses.
 *
 * The import dispatch itself (task polling, completion toast, invalidation) stays
 * with the caller's useReverseSync instance; this modal only orchestrates it.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/glitch-ui/spinner';
import { importChecksQueryOptions } from '@/lib/query-options';
import type { CampaignData } from '@/lib/types';

export function ImportCampaignModal({
  campaign,
  open,
  onClose,
  onImport,
}: {
  campaign: CampaignData;
  open: boolean;
  onClose: () => void;
  /** The useReverseSync import dispatch; polling and toasts live with the caller. */
  onImport: () => Promise<unknown>;
}) {
  const [isImporting, setIsImporting] = useState(false);

  const checksQuery = useQuery({
    ...importChecksQueryOptions(campaign.id),
    enabled: open,
  });
  const checks = checksQuery.data?.checks ?? [];
  const eligible = checksQuery.data?.eligible === true;

  // Fresh flow every open, so a retry after a failed attempt starts clean.
  useEffect(() => {
    if (open) setIsImporting(false);
  }, [open]);

  const handleImport = async () => {
    setIsImporting(true);
    try {
      await onImport();
      onClose();
    } catch {
      // The dispatch's own error toast has fired; keep the modal open for retry.
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isImporting && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Campaign</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            The campaign is checked against what the import supports before anything is pulled in. Importing makes
            this a fully managed campaign, optimizations included; its type is detected from the imported keywords
            and ad copy and can be changed on the Overview.
          </p>

          {checksQuery.isLoading && (
            <div className="flex items-center gap-2 py-6 justify-center text-gray-500">
              <Spinner size="sm" />
              <span className="text-sm">Running checks...</span>
            </div>
          )}

          {checksQuery.isError && (
            <div className="border-l-4 p-3 text-sm rounded-r-md border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C]">
              The checks could not be run. Please try again.
            </div>
          )}

          {!checksQuery.isLoading && !checksQuery.isError && (
            <ul className="space-y-2">
              {checks.map((check) => (
                <li key={check.id}>
                  {check.passed ? (
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#047857]" />
                      <span>{check.detail}</span>
                    </div>
                  ) : (
                    <div className="border-l-4 p-3 text-sm rounded-r-md border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{check.detail}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-between items-center pt-2">
            <Button
              variant="outline"
              disabled={isImporting}
              onClick={onClose}
              className="h-9 text-gray-900"
            >
              CANCEL
            </Button>
            {eligible ? (
              <Button
                disabled={isImporting}
                onClick={handleImport}
                className="bg-[#290BD9] hover:bg-[#2309C5] text-white h-9"
              >
                {isImporting ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    IMPORTING...
                  </>
                ) : (
                  'IMPORT'
                )}
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={checksQuery.isFetching}
                onClick={() => checksQuery.refetch()}
                className="h-9 text-gray-900"
              >
                RUN AGAIN
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
